import { QueryResult } from 'react-query';
import moment from 'moment-timezone';
import { defaultSmDate } from 'utils/formatter';
import { callApi, Controller, useApi, UseApiOptions } from './useApi';
// eslint-disable-next-line import/no-cycle
import { getKunde, Kunde } from './kunde';
// eslint-disable-next-line import/no-cycle
import { addAngebotPosition, AngebotPosition } from './angebotPosition';
import { getArtikelDetailKalkulationen } from './artikelDetailKalkulation';
// eslint-disable-next-line import/no-cycle
import { getArtikelDetail } from './artikelDetail';

export interface Angebot {
    kNr?: number;
    nr: string;
    auNr: string;
    kaNr: number;
    kaName: string;
    status: number;
    ablehnGrund: number;
    datum: string;
    datBis: string;
    betreuer: number;
    telBetreuer: number;
    txt: string;
    txtB1: number;
    txtB2: number;
    txtB3: number;
    txtB4: number;
    txtB5: number;
    ara: number;
    liefBed: number;
    liefDat: string;
    liefZeit: number;
    liefKw: number;
    zahlBed: number;
    mwstCode: number;
    sNetto: number;
    sMwst: number;
    sBrutto: number;
    extDoku: boolean;
    teilR: number;
    aktText: string;
    zusatz: string;
    anlageDat?: string;
    anlageUhr?: number;
    anlageUser?: string;
    updDat?: string;
    updUhr?: number;
    updUser?: string;

    kunde?: Kunde;
}

export function getDefaultAngebot(kNr?: number): Angebot {
    const values: Angebot = {
        kNr: undefined,
        nr: '',
        auNr: '',
        kaNr: 0,
        kaName: '',
        status: 0,
        ablehnGrund: 0,
        datum: defaultSmDate,
        datBis: defaultSmDate,
        betreuer: 0,
        telBetreuer: 0,
        txt: '',
        txtB1: 1,
        txtB2: 2,
        txtB3: 3,
        txtB4: 4,
        txtB5: 5,
        ara: 0,
        liefBed: 17,
        liefDat: defaultSmDate,
        liefZeit: 0,
        liefKw: 0,
        zahlBed: 1,
        mwstCode: 0,
        sNetto: 0,
        sMwst: 0,
        sBrutto: 0,
        extDoku: true,
        teilR: 0,
        aktText: '',
        zusatz: '',
    };

    const now = moment();

    return {
        ...values,
        kNr,
        datum: now.format('YYYY-MM-DD') + 'T00:00:00',
        datBis: now.clone().add({ days: 1 }).format('YYYY-MM-DD') + 'T00:00:00',
    };
}

export interface AngebotFilter {
    nr?: string;
    nrSearch?: string;
    kNr?: number;
    auNr?: string;
    auNrSearch?: string;
    artikelGruppeNr?: number;
    artikelNr?: string;
    searchText?: string;
    betreuer?: number;
    betreuerExact?: number;
    telBetreuerExact?: number;
    datumVon?: string;
    datumBis?: string;
    datBisVon?: string;
    datBisBis?: string;
    kundePlzVon?: string;
    kundePlzBis?: string;
    kundeUmsatzVon?: number | null;
    kundeUmsatzBis?: number | null;
    kundeBranchen?: number[];
    kundeErweiterteSuchen?: number[];
    kundeInteresseFuerProdukte?: number[];
    kundeBetreuerExact?: number;
    kundeTelBetreuerExact?: number;
    produktgruppen?: number[];
    status?: number;
    statusList?: number[];
    withKunde?: boolean;
    maxRows?: number;
    sortAsc?: boolean;
}

export function useAngebot(filter: AngebotFilter, apiOptions?: UseApiOptions<Angebot>): QueryResult<Angebot> {
    return useApi<Angebot>(Controller.angebot + '/one', { filter }, apiOptions);
}

export function useAngebotList(filter: AngebotFilter, apiOptions?: UseApiOptions<Angebot[]>): QueryResult<Angebot[]> {
    return useApi<Angebot[]>(Controller.angebot + '/list', { filter }, apiOptions);
}

interface AddAngebotResult {
    newNr: string;
}

export async function addAngebot(angebot: Angebot): Promise<AddAngebotResult> {
    return callApi<AddAngebotResult>(Controller.angebot + '/add', { angebot });
}

export async function updateAngebot(angebot: Angebot): Promise<void> {
    return callApi<void>(Controller.angebot + '/update', { angebot });
}

export async function deleteAngebot(angebot: Angebot): Promise<void> {
    return callApi<void>(Controller.angebot + '/delete', { angebot });
}

export async function sendAngebot(angebot: Angebot, kaNrListTo: number[], kaNrListCC: number[]): Promise<void> {
    return callApi<void>(Controller.angebot + '/send', { nr: angebot.nr, kaNrListTo, kaNrListCC });
}

export async function copyAngebot(
    kNr: number,
    angebot: Angebot,
    angebotPositionen?: AngebotPosition[],
): Promise<AddAngebotResult> {
    const newKundeList = await getKunde({ nr: kNr });

    if (newKundeList.length !== 1) {
        throw new Error('Kunde nicht gefunden');
    }

    const newKunde = newKundeList[0];

    const defaultAngebot = getDefaultAngebot();

    const newAngebot = {
        ...angebot,
        kNr,
        status: 0,
        auNr: '',
        datum: defaultAngebot.datum,
        datBis: defaultAngebot.datBis,
        betreuer: newKunde.betreuer || defaultAngebot.betreuer,
        telBetreuer: newKunde.telBetreuer || defaultAngebot.telBetreuer,
        zahlBed: newKunde.zahlZiel || defaultAngebot.zahlBed,
    };

    const result = await addAngebot(newAngebot);

    if (angebotPositionen) {
        // eslint-disable-next-line no-restricted-syntax
        for (const angebotPosition of angebotPositionen) {
            let ekPreis = angebotPosition.posEkPreis;
            try {
                // eslint-disable-next-line no-await-in-loop
                const artikelDetail = await getArtikelDetail({
                    artikelGruppeNr: angebotPosition.artikelGruppeNr,
                    artikelNr: angebotPosition.artikelNr,
                    includeArchiv: true,
                });

                // eslint-disable-next-line no-await-in-loop
                const artikelDetailKalkulationen = await getArtikelDetailKalkulationen({
                    artikelGruppeNr: angebotPosition.artikelGruppeNr,
                    artikelNr: angebotPosition.artikelNr,
                });

                if (artikelDetail && artikelDetailKalkulationen) {
                    const defaultKalkulation = artikelDetailKalkulationen.find(
                        (k) => k.eh === artikelDetail.artikel?.einheit,
                    );

                    if (defaultKalkulation?.ek2) {
                        ekPreis = defaultKalkulation.ek2;
                    }
                }
            } catch (error) {
                console.error(error);
            }

            // eslint-disable-next-line no-await-in-loop
            await addAngebotPosition({ ...angebotPosition, aNr: result.newNr, posEkPreis: ekPreis });
        }
    }

    return result;
}

interface RecalculateAngebotResult {
    angebot: Angebot;
}

export async function recalculateAngebot(angebot: Angebot): Promise<RecalculateAngebotResult> {
    return callApi<RecalculateAngebotResult>(Controller.angebot + '/recalculate', { angebot });
}
